import dayjs from 'dayjs'
import { ActivePeriod, SelectedDate, SelectedDateAction } from './types'
import { dateToWeek, weekToDate } from './utils'

export function selectedDateReducer(
  state: SelectedDate | undefined,
  action: SelectedDateAction
) {
  if (state === undefined) {
    return state
  }
  switch (action.type) {
    case 'today': {
      if (action.forceDay ?? state.activePeriod === 'day') {
        return {
          ...state,
          activePeriod: 'day' as ActivePeriod,
          date: dayjs(),
          week: dayjs().week(),
          year: dayjs().year(),
          teamspace: action.teamspace ?? state.teamspace,
        }
      }
      return {
        ...state,
        activePeriod: 'week' as ActivePeriod,
        ...dateToWeek(dayjs()),
        date: dayjs(),
        teamspace: action.teamspace ?? state.teamspace,
      }
    }
    case 'add': {
      if (state.activePeriod === 'week') {
        const newDate = weekToDate(state).add(action.amount, action.unit)
        return { ...state, week: newDate.week(), year: newDate.year() }
      }
      const newDate = state.date.add(action.amount, action.unit)
      return {
        ...state,
        date: newDate,
        week: newDate.week(),
        year: newDate.year(),
      }
    }
    case 'subtract': {
      if (state.activePeriod === 'week') {
        const newDate = weekToDate(state).subtract(action.amount, action.unit)
        return { ...state, week: newDate.week(), year: newDate.year() }
      }
      const newDate = state.date.subtract(action.amount, action.unit)
      return {
        ...state,
        date: newDate,
        week: newDate.week(),
        year: newDate.year(),
      }
    }
    case 'shift': {
      let newDate
      switch (state.activePeriod) {
        case 'week': {
          newDate = weekToDate(state).add(action.direction, 'week')
          return { ...state, week: newDate.week(), year: newDate.year() }
        }
        case 'month': {
          newDate = state.date.add(action.direction, 'month')
          return {
            ...state,
            date: newDate,
            week: newDate.week(),
            year: newDate.year(),
          }
        }
        case 'quarter': {
          newDate = state.date.add(action.direction * 3, 'month')
          return {
            ...state,
            date: newDate,
            week: newDate.week(),
            year: newDate.year(),
          }
        }
        case 'year': {
          newDate = state.date.add(action.direction, 'year')
          return {
            ...state,
            date: newDate,
            week: newDate.week(),
            year: newDate.year(),
          }
        }
        default: {
          newDate = state.date.add(action.direction, 'day')
          return {
            ...state,
            date: newDate,
            week: newDate.week(),
            year: newDate.year(),
          }
        }
      }
    }
    case 'transform': {
      const shouldTransform =
        action.to !== state.activePeriod || state.teamspace !== action.teamspace

      if (!shouldTransform) {
        return state
      }

      const validTimeframes = [
        'day',
        'week',
        'month',
        'quarter',
        'year',
      ] as const
      if (!validTimeframes.includes(action.to)) {
        return state
      }

      return {
        ...state,
        activePeriod: action.to,
        teamspace: action.teamspace,
      }
    }
    case 'setWeek': {
      return {
        ...state,
        activePeriod: 'week' as ActivePeriod,
        week: action.week,
        year: action.year,
        date: weekToDate(action),
        teamspace: action.teamspace ?? state.teamspace,
      }
    }
    case 'setDay': {
      return {
        ...state,
        activePeriod: 'day' as ActivePeriod,
        date: action.date,
        week: action.date.week(),
        year: action.date.year(),
        teamspace: action.teamspace ?? state.teamspace,
      }
    }
    case 'setMonth': {
      const newDate = dayjs()
        .year(action.year)
        .month(action.month)
        .startOf('month')
      return {
        ...state,
        activePeriod: 'month' as ActivePeriod,
        date: newDate,
        week: newDate.week(),
        year: action.year,
        teamspace: action.teamspace ?? state.teamspace,
      }
    }
    case 'setQuarter': {
      const newDate = dayjs()
        .year(action.year)
        .quarter(action.quarter)
        .startOf('quarter')
      return {
        ...state,
        activePeriod: 'quarter' as ActivePeriod,
        date: newDate,
        week: newDate.week(),
        year: action.year,
        teamspace: action.teamspace ?? state.teamspace,
      }
    }
    case 'setYear': {
      const newDate = dayjs().year(action.year).startOf('year')
      return {
        ...state,
        activePeriod: 'year' as ActivePeriod,
        date: newDate,
        week: newDate.week(),
        year: action.year,
        teamspace: action.teamspace ?? state.teamspace,
      }
    }
  }
}
